import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import type { JournalEntry, LifeContext, Language, AIPersonalityKey } from '../types';
import StoriesBar from './StoriesBar';
import InstagramMomentCard from './InstagramMomentCard';
import MomentSkeleton from './MomentSkeleton';
import StoryModal from './StoryModal';
import { useMomentAdvice } from '../hooks/useMomentAdvice';
import { useDailyGuide } from '../hooks/useDailyGuide';
import { PersonalityHeaderButton, PersonalitySelectorModal } from './PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

interface MomentAdvice {
  entryId: string;
  oracleInsight: string;
  shortTermAction: string;
  shortTermTimeframe: string;
  shortTermCompleted?: boolean;
  mediumTermGoal: string;
  mediumTermTimeframe: string;
  mediumTermCompleted?: boolean;
  longTermVision: string;
  longTermTimeframe: string;
  longTermCompleted?: boolean;
  emotionGradient: string;
  inspirationalQuote: string;
  connectedValues: string[];
  connectedGoals: string[];
}

interface WeeklyRecap {
  id: number;
  weekStart: Date;
  weekEnd: Date;
  oracleMessage: string;
  keyInsight: string;
  dominantEmotion?: string;
  topPattern?: string;
  recommendedAction?: string;
  weeklyScore?: number;
  entriesCount: number;
  isViewed: boolean;
}

interface InstagramFeedProps {
  context: LifeContext;
  entries: JournalEntry[];
  entriesLoading?: boolean;
  onDeleteEntry?: (id: string) => void;
  onLikeEntry?: (id: string, isLiked: boolean) => void;
  onPersonalityChange?: (key: AIPersonalityKey) => Promise<void>;
}

const TRANSLATIONS = {
  Italian: {
    title: 'Momenti',
    search: 'Cerca momenti...',
    noMoments: 'Nessun momento ancora',
    startJourney: 'Inizia a registrare i tuoi momenti',
    noResults: 'Nessun risultato trovato',
    loading: 'Caricamento momenti...',
  },
  English: {
    title: 'Moments',
    search: 'Search moments...',
    noMoments: 'No moments yet',
    startJourney: 'Start recording your moments',
    noResults: 'No results found',
    loading: 'Loading moments...',
  },
  Spanish: {
    title: 'Momentos',
    search: 'Buscar momentos...',
    noMoments: 'Sin momentos aún',
    startJourney: 'Comienza a registrar tus momentos',
    noResults: 'No se encontraron resultados',
    loading: 'Cargando momentos...',
  },
  German: {
    title: 'Momente',
    search: 'Momente suchen...',
    noMoments: 'Noch keine Momente',
    startJourney: 'Beginne deine Momente aufzuzeichnen',
    noResults: 'Keine Ergebnisse gefunden',
    loading: 'Momente werden geladen...',
  },
  French: {
    title: 'Moments',
    search: 'Rechercher des moments...',
    noMoments: 'Pas encore de moments',
    startJourney: 'Commence à enregistrer tes moments',
    noResults: 'Aucun résultat trouvé',
    loading: 'Chargement des moments...',
  },
};

const InstagramFeed: React.FC<InstagramFeedProps> = ({
  context,
  entries,
  entriesLoading = false,
  onDeleteEntry,
  onLikeEntry,
  onPersonalityChange,
}) => {
  const language = context.language as Language;
  const t = TRANSLATIONS[language] || TRANSLATIONS.English;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showLikedOnly, setShowLikedOnly] = useState(false);
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);

  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;

  // Hooks
  const {
    adviceMap,
    weeklyRecaps,
    generatingAdviceForIds,
    generateAdvice,
    completeAction,
    markRecapViewed,
  } = useMomentAdvice();

  const {
    dailyGuide,
    isLoading: isDailyGuideLoading,
    isGenerating: isGeneratingDailyGuide,
    isExpired: isDailyGuideExpired,
    generateDailyGuide,
    completeAction: completeDailyGuideAction,
  } = useDailyGuide();

  // Safe entries
  const safeEntries = Array.isArray(entries) ? entries : [];

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    safeEntries.forEach((e) => e.category && cats.add(e.category));
    return Array.from(cats);
  }, [safeEntries]);

  // Sort entries (newest first)
  const sortedEntries = useMemo(() => {
    return [...safeEntries].sort((a, b) => b.timestamp - a.timestamp);
  }, [safeEntries]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    return sortedEntries.filter((entry) => {
      // Search filter
      const matchesSearch =
        !searchQuery ||
        entry.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.category?.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory = !selectedCategory || entry.category === selectedCategory;

      // Liked filter
      const matchesLiked = !showLikedOnly || entry.isLiked === true;

      return matchesSearch && matchesCategory && matchesLiked;
    });
  }, [sortedEntries, searchQuery, selectedCategory, showLikedOnly]);

  // Get unviewed weekly recap
  const unviewedRecap = useMemo(() => {
    return weeklyRecaps.find((r) => !r.isViewed);
  }, [weeklyRecaps]);

  // Has unviewed guide
  const hasUnviewedGuide = !dailyGuide || isDailyGuideExpired();

  // Track previous entry count to detect new entries
  const prevEntryCountRef = useRef(safeEntries.length);

  // Auto-generate guide on mount (login) if needed and user has entries
  useEffect(() => {
    const shouldGenerate = safeEntries.length > 0 && (!dailyGuide || isDailyGuideExpired());
    if (shouldGenerate && !isDailyGuideLoading && !isGeneratingDailyGuide) {
      generateDailyGuide();
    }
  }, []); // Only on mount (login)

  // Auto-regenerate guide when new entries are added
  useEffect(() => {
    const currentCount = safeEntries.length;
    const prevCount = prevEntryCountRef.current;

    // If entry count increased (new moment added) and we have entries
    if (currentCount > prevCount && currentCount > 0) {
      // Generate new guide if expired or doesn't exist
      if (!dailyGuide || isDailyGuideExpired()) {
        if (!isDailyGuideLoading && !isGeneratingDailyGuide) {
          generateDailyGuide();
        }
      }
    }

    // Update ref for next comparison
    prevEntryCountRef.current = currentCount;
  }, [safeEntries.length, dailyGuide, isDailyGuideExpired, isDailyGuideLoading, isGeneratingDailyGuide, generateDailyGuide]);

  // Handlers
  const handleLike = useCallback(
    async (id: string, isLiked: boolean) => {
      if (onLikeEntry) {
        onLikeEntry(id, isLiked);
      }
    },
    [onLikeEntry]
  );

  const handleGenerateAdvice = useCallback(
    async (entry: JournalEntry) => {
      await generateAdvice(entry);
    },
    [generateAdvice]
  );

  const handleOpenGuideModal = useCallback(() => {
    // Always open the modal - it will show loading state if generating
    setShowGuideModal(true);
    // If no guide or expired, generate a new one
    if (!dailyGuide || isDailyGuideExpired()) {
      if (!isGeneratingDailyGuide) {
        generateDailyGuide();
      }
    }
  }, [dailyGuide, isDailyGuideExpired, generateDailyGuide, isGeneratingDailyGuide]);

  const handleViewWeeklyRecap = useCallback(() => {
    if (unviewedRecap) {
      setShowWeeklyModal(true);
      markRecapViewed(unviewedRecap.id);
    }
  }, [unviewedRecap, markRecapViewed]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header - Instagram Style */}
      <header className="sticky top-0 z-30 bg-white border-b border-black/5">
        <div className="h-11 px-4 flex items-center justify-between">
          {showSearch ? (
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                <input
                  type="text"
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 bg-black/5 rounded-lg text-[15px] placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-black/10"
                />
              </div>
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery('');
                }}
                className="p-2 text-black/60"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-[30px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none">{t.title}</h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSearch(true)}
                  className="p-2 text-black/70 hover:text-black transition-colors"
                >
                  <Search size={22} />
                </button>
                <PersonalityHeaderButton
                  personalityKey={currentPersonalityKey}
                  language={language}
                  onClick={() => setIsPersonalityModalOpen(true)}
                />
              </div>
            </>
          )}
        </div>

        {/* Stories Bar */}
        <StoriesBar
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={(cat) => {
            setSelectedCategory(cat);
            setShowLikedOnly(false);
          }}
          showLikedFilter={true}
          isLikedFilterActive={showLikedOnly}
          onToggleLikedFilter={() => {
            setShowLikedOnly(!showLikedOnly);
            setSelectedCategory(null);
          }}
          hasUnviewedGuide={hasUnviewedGuide}
          onOpenGuide={handleOpenGuideModal}
          language={language}
        />
      </header>

      {/* Feed Content */}
      <div className="flex-1 overflow-y-auto pb-24">
        {/* Weekly Recap Banner (if unviewed) */}
        {unviewedRecap && (
          <button
            onClick={handleViewWeeklyRecap}
            className="w-full p-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white text-left"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider opacity-80 mb-1">
              Weekly Oracle
            </p>
            <p className="text-[15px] font-medium line-clamp-2">
              "{unviewedRecap.oracleMessage}"
            </p>
          </button>
        )}

        {/* Loading State */}
        {entriesLoading && filteredEntries.length === 0 && (
          <MomentSkeleton count={3} />
        )}

        {/* Empty State */}
        {!entriesLoading && filteredEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-black/5 flex items-center justify-center mb-4">
              <span className="text-4xl">✨</span>
            </div>
            <h3 className="text-lg font-semibold text-black/80 mb-2">
              {searchQuery || selectedCategory || showLikedOnly
                ? t.noResults
                : t.noMoments}
            </h3>
            <p className="text-[14px] text-black/50">
              {!searchQuery && !selectedCategory && !showLikedOnly && t.startJourney}
            </p>
          </div>
        )}

        {/* Moments Feed */}
        {filteredEntries.map((entry) => (
          <InstagramMomentCard
            key={entry.id}
            entry={entry}
            advice={adviceMap.get(entry.id) || null}
            language={language}
            onLike={handleLike}
            onDelete={onDeleteEntry}
            onGenerateAdvice={handleGenerateAdvice}
            onCompleteAction={completeAction}
            isGeneratingAdvice={generatingAdviceForIds.has(entry.id)}
            connections={entry.relatedTo || []}
            allEntries={safeEntries}
          />
        ))}
      </div>

      {/* Daily Guide Story Modal */}
      <StoryModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        type="daily"
        dailyGuide={dailyGuide}
        onRefreshGuide={() => generateDailyGuide(true)}
        onCompleteAction={completeDailyGuideAction}
        isRefreshing={isGeneratingDailyGuide}
        language={language}
      />

      {/* Weekly Recap Story Modal */}
      <StoryModal
        isOpen={showWeeklyModal}
        onClose={() => setShowWeeklyModal(false)}
        type="weekly"
        weeklyRecap={unviewedRecap}
        language={language}
      />

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange!}
        language={language}
      />
    </div>
  );
};

export default InstagramFeed;
